import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import LoadingSpinner from '@src/components/LoadingSpinner'
import { useAuth } from '@src/context/AuthContext'
import { useCart } from '@src/context/CartContext'
import { useProducts } from '@src/hooks/queries/useProducts'
import { useProductOptions } from '@src/hooks/queries/useOptions'
import { usePublicProductTypes } from '@src/hooks/queries/useProductTypes'
import { usePublicFeaturedProducts } from '@src/hooks/queries/useFeaturedProducts'
import { usePublicManufacturers } from '@src/hooks/queries/useManufacturers'
import { useCategoryImageMap } from '@src/hooks/useCategoryImageMap'

import type { CatalogProduct, FeaturedProduct } from '@src/types'
import ProductsPageHeader from '@design-components/ProductsPageHeader'
import ProductFlyerModal from '@design-components/ProductFlyerModal'
import FeaturedProductCard from '@design-components/FeaturedProductCard'
import SaleCornerBadge from '@src/components/SaleCornerBadge'
import WshHelpTrigger from '@wsh/WshHelpTrigger'
import NotificationBanner from '@design-components/NotificationBanner'
import { IS_DEMO } from '@src/constants/env'
import LoginModal from '@src/features/auth/LoginModal'
import { getProductImage, handleImageError } from '@src/utils/productImages'
import {
  colors, transitions,
  PaginationControls, PageButton, PaginationFooter, ResultCount,
} from '@src/theme/sharedComponents'
import styled from 'styled-components'

const formatPrice = (price: number | null | undefined) => {
  if (price == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price)
}

type SortField = 'sku' | 'manufacturer' | 'category' | 'price'

// ─── Page shell ───────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  background: ${colors.background};
  flex: 1;
  display: flex;
  flex-direction: column;
`

// ─── Products section ─────────────────────────────────────────────────────────

const ProductsSection = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 1.25rem 2rem 6rem;

  @media (max-width: 575px) {
    padding: 1rem 0.75rem 6rem;
  }
`

// ─── Filter bar ───────────────────────────────────────────────────────────────

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
`

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.textLightModeMuted};
    font-size: 0.9rem;
    pointer-events: none;
  }
`

const SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 2.1rem 0.6rem 2.25rem;
  border: 1.5px solid ${colors.borderLight};
  border-radius: var(--radius);
  background: white;
  color: ${colors.textLightMode};
  font-size: 0.9rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder { color: ${colors.textLightModeMuted}; }

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-primary) 10%, transparent);
  }
`

const SearchClearBtn = styled.button`
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.1rem 0.2rem;
  cursor: pointer;
  color: ${colors.textLightModeMuted};
  font-size: 0.85rem;
  line-height: 1;
  display: flex;
  align-items: center;
  border-radius: var(--radius-xs);
  transition: color 0.15s ease;

  &:hover { color: ${colors.textLightMode}; }
`

// ─── Filter chip row ──────────────────────────────────────────────────────────

const FilterChipRow = styled.div<{ $visible?: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;

  @media (max-width: 900px) {
    display: ${p => p.$visible ? 'flex' : 'none'};
  }
`

const FilterControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.75rem;
`

const MobileFilterToggle = styled.button`
  display: none;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border: 1.5px solid ${colors.borderLight};
  border-radius: var(--radius-sm);
  background: white;
  color: ${colors.primary};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: ${transitions.fast};

  i { font-size: 0.85rem; }

  &:active {
    border-color: ${colors.primary};
    background: color-mix(in srgb, var(--color-primary) 5%, transparent);
  }

  @media (max-width: 900px) {
    display: inline-flex;
  }
`

const SortChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.85rem;
  border-radius: var(--radius-pill);
  border: 1.5px solid ${p => p.$active ? colors.primary : colors.borderLight};
  background: ${p => p.$active ? 'color-mix(in srgb, var(--color-primary) 8%, transparent)' : 'white'};
  color: ${p => p.$active ? colors.primary : colors.textLightModeMuted};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: ${transitions.fast};

  i { font-size: 0.8rem; }

  &:hover {
    border-color: ${colors.primary};
    color: ${colors.primary};
    background: color-mix(in srgb, var(--color-primary) 5%, transparent);
  }
`

const SortDirPill = styled.span`
  font-size: 0.6rem;
  font-weight: 500;
  background: color-mix(in srgb, var(--color-primary) 12%, transparent);
  color: ${colors.primary};
  padding: 0.1rem 0.3rem;
  border-radius: var(--radius-xs);
  line-height: 1.4;
  letter-spacing: 0;
`

const FilterChipGroup = styled.div<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  border-radius: var(--radius-pill);
  border: 1.5px solid ${p => p.$active ? colors.primary : colors.borderLight};
  background: ${p => p.$active ? 'color-mix(in srgb, var(--color-primary) 6%, transparent)' : 'white'};
  overflow: hidden;
  transition: border-color 0.15s;

  &:hover { border-color: ${colors.primary}; }
`

const FilterChipSortBtn = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.5rem 0.4rem 0.85rem;
  border: none;
  border-right: 1px solid ${colors.borderLight};
  background: none;
  color: ${p => p.$active ? colors.primary : colors.textLightModeMuted};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s;

  i { font-size: 0.8rem; }

  &:hover { color: ${colors.primary}; }
`

const FilterChipSelect = styled.select<{ $active?: boolean }>`
  border: none;
  background: none;
  color: ${p => p.$active ? colors.primary : colors.textLightModeMuted};
  font-size: 0.75rem;
  font-weight: ${p => p.$active ? '600' : '400'};
  padding: 0.4rem 0.65rem 0.4rem 0.4rem;
  cursor: pointer;
  max-width: 110px;
  transition: color 0.15s;

  &:focus { outline: none; }

  option {
    background: white;
    color: ${colors.textLightMode};
    font-weight: 400;
    font-size: 0.85rem;
  }
`

const SaleChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.85rem;
  border-radius: var(--radius-pill);
  border: 1.5px solid ${p => p.$active ? colors.primary : colors.borderLight};
  background: ${p => p.$active ? colors.primary : 'white'};
  color: ${p => p.$active ? 'white' : colors.textLightModeMuted};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: ${transitions.fast};

  i { font-size: 0.8rem; }

  &:hover {
    border-color: ${colors.primary};
    background: ${p => p.$active ? colors.primaryLight : 'color-mix(in srgb, var(--color-primary) 5%, transparent)'};
    color: ${p => p.$active ? 'white' : colors.primary};
  }
`

const ViewToggleGroup = styled.div`
  display: inline-flex;
  border: 1.5px solid ${colors.borderLight};
  border-radius: var(--radius-pill);
  overflow: hidden;
  margin-left: auto;

  @media (max-width: 480px) { display: none; }
`

const ViewToggleBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  border: none;
  background: ${p => p.$active ? colors.primary : 'white'};
  color: ${p => p.$active ? 'white' : colors.textLightModeMuted};
  font-size: 0.85rem;
  cursor: pointer;
  transition: ${transitions.fast};

  &:first-child { border-right: 1px solid ${colors.borderLight}; }

  &:hover {
    background: ${p => p.$active ? colors.primary : 'color-mix(in srgb, var(--color-primary) 5%, transparent)'};
    color: ${p => p.$active ? 'white' : colors.primary};
  }
`

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  gap: 0.75rem;
  color: ${colors.textLightModeMuted};
  text-align: center;

  i { font-size: 2.5rem; opacity: 0.4; }
  p { margin: 0; font-size: 0.95rem; }
`

const ClearFiltersBtn = styled.button`
  padding: 0.5rem 1.25rem;
  border: 1.5px solid ${colors.primary};
  border-radius: var(--radius);
  background: white;
  color: ${colors.primary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: ${transitions.fast};

  &:hover { background: ${colors.primary}; color: white; }
`

// ─── Shared ───────────────────────────────────────────────────────────────────

const CardTag = styled.div<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 500;
  color: ${p => p.$primary ? colors.primary : colors.textLightModeMuted};
  background: ${p => p.$primary ? 'color-mix(in srgb, var(--color-primary) 8%, transparent)' : colors.backgroundAlt};
  border-radius: var(--radius-xs);
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
`

const CardShortDesc = styled.div`
  font-size: 0.78rem;
  color: ${colors.textLightModeMuted};
  line-height: 1.3;
  margin-top: 0.15rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`

const PriceNow = styled.span<{ $sale?: boolean }>`
  font-weight: 700;
  color: ${p => p.$sale ? colors.success : colors.primaryDark};
  line-height: 1;
`

const PriceWas = styled.span`
  font-size: 0.8rem;
  color: ${colors.textLightModeMuted};
  text-decoration: line-through;
  white-space: nowrap;
`

const SaveLabel = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  background: ${colors.primary};
  color: #fff;
  padding: 0.12rem 0.45rem;
  border-radius: var(--radius-pill);
  white-space: nowrap;
`

const InCartBar = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: ${colors.success}12;
  border: none;
  border-top: 1px solid ${colors.success}25;
  color: ${colors.success};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
  flex-shrink: 0;

  &:hover { background: ${colors.success}22; }
`

// ─── Grid ─────────────────────────────────────────────────────────────────────

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.625rem;
  }
`

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const GridCard = styled.div`
  background: white;
  border: 1px solid ${colors.borderLight};
  border-radius: var(--radius);
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: ${colors.primary};
    box-shadow: 0 6px 24px color-mix(in srgb, var(--color-primary) 13%, transparent);
    transform: translateY(-2px);
  }
`

const GridCardImg = styled.div`
  aspect-ratio: 1 / 1;
  background: linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 4%, transparent) 0%, ${colors.backgroundAlt} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  position: relative;
  border-bottom: 1px solid ${colors.borderLight};

  img { max-width: 100%; max-height: 100%; object-fit: contain; }
`

const GridCardBody = styled.div`
  padding: 0.75rem 0.875rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1;
`

const GridMfr = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: ${colors.textLightModeMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const GridName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${colors.textLightMode};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const GridPricing = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: 0.1rem;

  ${PriceNow} { font-size: 1.15rem; }
`

// ─── List ─────────────────────────────────────────────────────────────────────

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`

const ListRow = styled.div`
  background: white;
  border: 1px solid ${colors.borderLight};
  border-radius: var(--radius);
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: ${colors.primary};
    box-shadow: 0 4px 18px color-mix(in srgb, var(--color-primary) 10%, transparent);
    transform: translateY(-1px);
  }
`

const ViewDetailsOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: white;
  background: color-mix(in srgb, var(--color-primary) 52%, transparent);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  ${GridCard}:hover &,
  ${ListRow}:hover & {
    opacity: 1;
  }
`

const ListRowContent = styled.div`
  display: flex;
  align-items: center;
`

const ListImg = styled.div`
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 4%, transparent) 0%, ${colors.backgroundAlt} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  border-right: 1px solid ${colors.borderLight};
  position: relative;

  img { max-width: 100%; max-height: 100%; object-fit: contain; }
`

const ListInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.875rem 1rem;
`

const ListMfr = styled.div`
  font-size: 0.72rem;
  font-weight: 500;
  color: ${colors.textLightModeMuted};
`

const ListName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.textLightMode};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const ListPrice = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.875rem 1.25rem;

  ${PriceNow} { font-size: 1.3rem; }
`

// ─── Results bar ──────────────────────────────────────────────────────────────

const ResultsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`

const ActivePills = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
`

const ActivePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  border-radius: var(--radius-pill);
  border: 1.5px solid ${colors.primary};
  background: color-mix(in srgb, var(--color-primary) 8%, transparent);
  color: ${colors.primary};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: ${transitions.fast};

  i { font-size: 0.65rem; }

  &:hover { background: color-mix(in srgb, var(--color-primary) 15%, transparent); }
`

// ─────────────────────────────────────────────────────────────────────────────

const ProductsPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth()
  const { items: cartItems, openCart } = useCart()
  const cartProductIds = useMemo(() => new Set(cartItems.map((i) => i.product_id)), [cartItems])
  const cartQtyMap = useMemo(() => Object.fromEntries(cartItems.map((i) => [i.product_id, i.quantity])), [cartItems])
  const [searchParams, setSearchParams] = useSearchParams()


  // searchInput is local state for the debounce buffer only
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') ?? '')
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') ?? '')

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setSearchParams(prev => {
        if (searchInput) prev.set('q', searchInput); else prev.delete('q')
        prev.delete('page')
        return prev
      }, { replace: true })
    }, 300)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  // All filter state derived directly from URL
  const categoryFilter = searchParams.get('category') ?? ''
  const manufacturerFilter = searchParams.get('manufacturer') ?? ''
  const productTypeFilter = searchParams.get('product_type') ?? ''
  const showOnSaleOnly = searchParams.get('filter') === 'sale'
  const showFeaturedOnly = searchParams.get('filter') === 'featured'
  const currentPage = parseInt(searchParams.get('page') ?? '1', 10)
  const sortField = (searchParams.get('sort') as SortField | null) ?? null
  const sortDir = (searchParams.get('dir') as 'asc' | 'desc') ?? 'asc'

  const setFilter = useCallback((key: string, value: string | null) => {
    setSearchParams(prev => {
      if (value) prev.set(key, value); else prev.delete(key)
      prev.delete('page')
      return prev
    }, { replace: true })
  }, [setSearchParams])

  const setCurrentPage = useCallback((page: number) => {
    setSearchParams(prev => {
      if (page > 1) prev.set('page', String(page)); else prev.delete('page')
      return prev
    }, { replace: true })
  }, [setSearchParams])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentPage])

  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | FeaturedProduct | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() =>
    window.innerWidth <= 480 ? 'grid' : 'list'
  )
  const [showFilters, setShowFilters] = useState(false)

  const openLoginModal = useCallback(() => {
    setSearchParams(prev => { prev.set('login', 'true'); return prev })
  }, [setSearchParams])

  const clearFilters = useCallback(() => {
    setSearchInput('')
    setSearchQuery('')
    setSearchParams(prev => {
      prev.delete('q')
      prev.delete('category')
      prev.delete('manufacturer')
      prev.delete('product_type')
      prev.delete('filter')
      prev.delete('sort')
      prev.delete('dir')
      prev.delete('page')
      return prev
    }, { replace: true })
  }, [setSearchParams])

  const { data: productsData, isLoading: productsLoading } = useProducts({
    page: currentPage,
    perPage: 20,
    search: searchQuery,
    category: categoryFilter,
    manufacturer: manufacturerFilter,
    productType: productTypeFilter,
    includeInactive: false,
  })

  const { data: optionsData } = useProductOptions()
  const { data: productTypesData } = usePublicProductTypes({ category: categoryFilter || undefined, enabled: !!categoryFilter })
  const productTypes = productTypesData?.product_types || []
  const { data: featuredData } = usePublicFeaturedProducts()
  const categoryImageMap = useCategoryImageMap()
  const { data: manufacturersData } = usePublicManufacturers()
  const mfrImageMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const m of manufacturersData?.manufacturers || []) {
      if (m.image_url) map[m.name] = m.image_url
    }
    return map
  }, [manufacturersData])

  const allProducts = productsData?.products || []

  const onSaleProducts = useMemo(() => {
    return (productsData?.products || []).filter((p) => p.sale_price)
  }, [productsData])

  const featuredSlots = useMemo(() => {
    return (featuredData?.products || []).filter((fp) => fp.product)
  }, [featuredData])

  const products = showFeaturedOnly ? featuredSlots.map(fp => fp.product!) : showOnSaleOnly ? onSaleProducts : allProducts

  const sortedProducts = useMemo(() => {
    if (!sortField) return products
    return [...products].sort((a, b) => {
      let aVal: string | number = ''
      let bVal: string | number = ''
      if (sortField === 'sku') {
        aVal = a.sku.toLowerCase(); bVal = b.sku.toLowerCase()
      } else if (sortField === 'manufacturer') {
        aVal = (a.manufacturer || '').toLowerCase(); bVal = (b.manufacturer || '').toLowerCase()
      } else if (sortField === 'category') {
        aVal = (a.category || '').toLowerCase(); bVal = (b.category || '').toLowerCase()
      } else if (sortField === 'price') {
        aVal = a.sale_price ?? a.price ?? 0; bVal = b.sale_price ?? b.price ?? 0
      }
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal)
      }
      return sortDir === 'asc' ? aVal - (bVal as number) : (bVal as number) - aVal
    })
  }, [products, sortField, sortDir])

  // Three-cycle: first click = asc, second = desc, third = reset to null
  const handleSort = useCallback((field: SortField) => {
    setSearchParams(prev => {
      const prevField = prev.get('sort') as SortField | null
      const prevDir = (prev.get('dir') as 'asc' | 'desc') ?? 'asc'
      if (prevField === field) {
        if (prevDir === 'desc') { prev.delete('sort'); prev.delete('dir') }
        else prev.set('dir', 'desc')
      } else {
        prev.set('sort', field)
        prev.set('dir', 'asc')
      }
      prev.delete('page')
      return prev
    }, { replace: true })
  }, [setSearchParams])

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return <i className="bi bi-sort" />
    return sortDir === 'asc' ? <i className="bi bi-sort-up" /> : <i className="bi bi-sort-down" />
  }

  const sortDirLabel = (field: SortField) => {
    if (sortField !== field) return null
    if (field === 'price') {
      return <SortDirPill>{sortDir === 'asc' ? 'Low→High' : 'High→Low'}</SortDirPill>
    }
    return <SortDirPill>{sortDir === 'asc' ? 'A→Z' : 'Z→A'}</SortDirPill>
  }

  const hasActiveFilters = !!(searchQuery || categoryFilter || manufacturerFilter || productTypeFilter || showOnSaleOnly || showFeaturedOnly || sortField)

  const totalCount = showFeaturedOnly
    ? featuredSlots.length
    : showOnSaleOnly
    ? onSaleProducts.length
    : productsData?.total ?? 0

  const resultText = showFeaturedOnly || showOnSaleOnly || !productsData || productsData.pages <= 1
    ? `${totalCount} product${totalCount !== 1 ? 's' : ''}`
    : `Showing ${allProducts.length} of ${totalCount} products · Page ${currentPage} of ${productsData.pages}`

  const activePills = [
    categoryFilter ? { label: `Category: ${categoryFilter}`, onClear: () => setFilter('category', null) } : null,
    productTypeFilter ? { label: `Type: ${productTypeFilter}`, onClear: () => setFilter('product_type', null) } : null,
    manufacturerFilter ? { label: `Mfr: ${manufacturerFilter}`, onClear: () => setFilter('manufacturer', null) } : null,
    showOnSaleOnly ? { label: 'On Sale', onClear: () => setFilter('filter', null) } : null,
    showFeaturedOnly ? { label: 'Featured', onClear: () => setFilter('filter', null) } : null,
  ].filter(Boolean) as { label: string; onClear: () => void }[]

  return (
    <>
      <PageWrapper>

        {IS_DEMO && (
          <NotificationBanner
            variant="accent"
            message={<>You're exploring a live read-only demo, nothing you do here affects real data. <a href="https://computeranything.dev/contact?projectType=wholesale-hub-demo" target="_blank" rel="noopener noreferrer">Get your own instance →</a></>}
          />
        )}

        <ProductsPageHeader onProductClick={setSelectedProduct} />

        <ProductsSection>
          <FilterBar>
            <SearchWrapper data-tour="products-search">
              <i className="bi bi-search search-icon" />
              <SearchInput
                type="text"
                placeholder="Search by SKU, part #, or description..."
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setCurrentPage(1) }}
              />
              {searchInput && (
                <SearchClearBtn
                  onClick={() => { setSearchInput(''); setSearchQuery(''); setCurrentPage(1) }}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg" />
                </SearchClearBtn>
              )}
            </SearchWrapper>
          </FilterBar>

          <FilterChipRow $visible={showFilters} data-tour="products-filters">
            {/* ── Sort chips ── */}
            <SortChip $active={sortField === 'sku'} onClick={() => handleSort('sku')}>
              SKU {sortIcon('sku')} {sortDirLabel('sku')}
            </SortChip>

            <FilterChipGroup $active={sortField === 'manufacturer' || !!manufacturerFilter}>
              <FilterChipSortBtn $active={sortField === 'manufacturer'} onClick={() => handleSort('manufacturer')}>
                Mfr {sortIcon('manufacturer')} {sortDirLabel('manufacturer')}
              </FilterChipSortBtn>
              {(optionsData?.manufacturers?.length ?? 0) > 0 && (
                <FilterChipSelect
                  $active={!!manufacturerFilter}
                  value={manufacturerFilter}
                  title="Filter by manufacturer"
                  onChange={(e) => setFilter('manufacturer', e.target.value || null)}
                >
                  <option value="">All</option>
                  {optionsData!.manufacturers.map(mfr => (
                    <option key={mfr.id} value={mfr.name}>{mfr.name}</option>
                  ))}
                </FilterChipSelect>
              )}
            </FilterChipGroup>

            <FilterChipGroup $active={sortField === 'category' || !!categoryFilter}>
              <FilterChipSortBtn $active={sortField === 'category'} onClick={() => handleSort('category')}>
                Category {sortIcon('category')} {sortDirLabel('category')}
              </FilterChipSortBtn>
              {(optionsData?.categories?.length ?? 0) > 0 && (
                <FilterChipSelect
                  $active={!!categoryFilter}
                  value={categoryFilter}
                  title="Filter by category"
                  onChange={(e) => {
                    setFilter('category', e.target.value || null)
                    setFilter('product_type', null)
                  }}
                >
                  <option value="">All</option>
                  {optionsData!.categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </FilterChipSelect>
              )}
            </FilterChipGroup>

            {categoryFilter && productTypes.length > 0 && (
              <FilterChipGroup $active={!!productTypeFilter}>
                <FilterChipSelect
                  $active={!!productTypeFilter}
                  value={productTypeFilter}
                  title="Filter by product type"
                  onChange={(e) => setFilter('product_type', e.target.value || null)}
                >
                  <option value="">All Types</option>
                  {productTypes.map(pt => (
                    <option key={pt.id} value={pt.name}>{pt.name}</option>
                  ))}
                </FilterChipSelect>
              </FilterChipGroup>
            )}

            <SortChip $active={sortField === 'price'} onClick={() => handleSort('price')}>
              Price {sortIcon('price')} {sortDirLabel('price')}
            </SortChip>

            <SaleChip $active={showFeaturedOnly} onClick={() => setFilter('filter', showFeaturedOnly ? null : 'featured')}>
              <i className="bi bi-star-fill" /> Featured
            </SaleChip>

            <SaleChip $active={showOnSaleOnly} onClick={() => setFilter('filter', showOnSaleOnly ? null : 'sale')}>
              <i className="bi bi-lightning-fill" /> On Sale
            </SaleChip>
          </FilterChipRow>

          <FilterControlBar>
            <MobileFilterToggle data-tour="products-filter-mobile" onClick={() => setShowFilters(!showFilters)}>
              <i className="bi bi-funnel" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </MobileFilterToggle>
            <ViewToggleGroup>
              <ViewToggleBtn $active={viewMode === 'list'} onClick={() => setViewMode('list')} title="List view">
                <i className="bi bi-list-ul" />
              </ViewToggleBtn>
              <ViewToggleBtn $active={viewMode === 'grid'} onClick={() => setViewMode('grid')} title="Grid view">
                <i className="bi bi-grid-3x3-gap-fill" />
              </ViewToggleBtn>
            </ViewToggleGroup>
          </FilterControlBar>

          <ResultsBar data-tour="products-grid">
            <ResultCount>{resultText}</ResultCount>
            {activePills.length > 0 && (
              <ActivePills>
                {activePills.map((pill, i) => (
                  <ActivePill key={i} onClick={pill.onClear}>
                    {pill.label} <i className="bi bi-x-lg" />
                  </ActivePill>
                ))}
              </ActivePills>
            )}
          </ResultsBar>

          {productsLoading && allProducts.length === 0 ? (
            <LoadingSpinner />
          ) : products.length === 0 ? (
            <EmptyState>
              <i className="bi bi-search" />
              <p>No products match your current filters.</p>
              {hasActiveFilters && (
                <ClearFiltersBtn onClick={clearFilters}>Clear all filters</ClearFiltersBtn>
              )}
            </EmptyState>
          ) : showFeaturedOnly ? (
            <FeaturedGrid>
              {featuredSlots.map(fp => (
                <FeaturedProductCard
                  key={fp.id}
                  fp={fp}
                  onClick={() => setSelectedProduct(fp)}
                  categoryImageMap={categoryImageMap}
                />
              ))}
            </FeaturedGrid>
          ) : viewMode === 'list' ? (
            <ProductList>
              {sortedProducts.map(product => {
                const salePrice = product.sale_price
                const inCart = cartProductIds.has(product.id)
                const imgSrc = getProductImage(product.image_url, product.category, categoryImageMap)
                return (
                  <ListRow key={product.id} onClick={() => setSelectedProduct(product)}>
                    <ListRowContent>
                      <ListImg>
                        <img
                          src={imgSrc}
                          alt={product.product_type}
                          onError={(e) => handleImageError(e, product.category, categoryImageMap)}
                        />
                        {salePrice && <SaleCornerBadge />}
                        <ViewDetailsOverlay>
                          <i className="bi bi-eye" />
                          View Details
                        </ViewDetailsOverlay>
                      </ListImg>
                      <ListInfo>
                        {product.manufacturer && (
                          mfrImageMap[product.manufacturer] ? (
                            <img
                              src={mfrImageMap[product.manufacturer]}
                              alt={product.manufacturer}
                              style={{ height: '20px', maxWidth: '64px', objectFit: 'contain' }}
                            />
                          ) : (
                            <ListMfr>{product.manufacturer}</ListMfr>
                          )
                        )}
                        <ListName>{product.sku}</ListName>
                        {product.short_description && <CardShortDesc>{product.short_description}</CardShortDesc>}
                        <CardMeta>
                          {product.category && <CardTag $primary>{product.category}</CardTag>}
                          {product.product_type && <CardTag>{product.product_type}</CardTag>}
                        </CardMeta>
                      </ListInfo>
                      <ListPrice>
                        <PriceNow $sale={!!salePrice}>{formatPrice(salePrice ?? product.price)}</PriceNow>
                        {salePrice && product.price && (
                          <>
                            <PriceWas>{formatPrice(product.price)}</PriceWas>
                            <SaveLabel>Save {Math.round((1 - salePrice / product.price) * 100)}%</SaveLabel>
                          </>
                        )}
                      </ListPrice>
                    </ListRowContent>
                    {inCart && (
                      <InCartBar onClick={e => { e.stopPropagation(); openCart() }}>
                        <i className="bi bi-cart-check" /> ({cartQtyMap[product.id]}) View Cart
                      </InCartBar>
                    )}
                  </ListRow>
                )
              })}
            </ProductList>
          ) : (
            <ProductGrid>
              {sortedProducts.map(product => {
                const salePrice = product.sale_price
                const inCart = cartProductIds.has(product.id)
                const imgSrc = getProductImage(product.image_url, product.category, categoryImageMap)
                return (
                  <GridCard key={product.id} onClick={() => setSelectedProduct(product)}>
                    <GridCardImg>
                      <img
                        src={imgSrc}
                        alt={product.product_type}
                        onError={(e) => handleImageError(e, product.category, categoryImageMap)}
                      />
                      {salePrice && <SaleCornerBadge />}
                      <ViewDetailsOverlay>
                        <i className="bi bi-eye" />
                        View Details
                      </ViewDetailsOverlay>
                    </GridCardImg>
                    <GridCardBody>
                      {product.manufacturer && (
                        mfrImageMap[product.manufacturer] ? (
                          <img
                            src={mfrImageMap[product.manufacturer]}
                            alt={product.manufacturer}
                            style={{ height: '18px', maxWidth: '60px', objectFit: 'contain' }}
                          />
                        ) : (
                          <GridMfr>{product.manufacturer}</GridMfr>
                        )
                      )}
                      <GridName>{product.sku}</GridName>
                      {product.short_description && <CardShortDesc>{product.short_description}</CardShortDesc>}
                      <GridPricing>
                        <PriceNow $sale={!!salePrice}>{formatPrice(salePrice ?? product.price)}</PriceNow>
                        {salePrice && product.price && (
                          <>
                            <PriceWas>{formatPrice(product.price)}</PriceWas>
                            <SaveLabel>Save {Math.round((1 - salePrice / product.price) * 100)}%</SaveLabel>
                          </>
                        )}
                      </GridPricing>
                      <CardMeta>
                        {product.category && <CardTag $primary>{product.category}</CardTag>}
                        {product.product_type && <CardTag>{product.product_type}</CardTag>}
                      </CardMeta>
                    </GridCardBody>
                    {inCart && (
                      <InCartBar onClick={e => { e.stopPropagation(); openCart() }}>
                        <i className="bi bi-cart-check" /> ({cartQtyMap[product.id]}) View Cart
                      </InCartBar>
                    )}
                  </GridCard>
                )
              })}
            </ProductGrid>
          )}

          {!showOnSaleOnly && !showFeaturedOnly && (
            <PaginationFooter>
              <ResultCount>
                {productsData ? `Page ${currentPage} of ${productsData.pages} (${totalCount} total)` : '—'}
              </ResultCount>
              <PaginationControls>
                <PageButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1 || !productsData || productsData.pages <= 1} title="First page">
                  <i className="bi bi-chevron-double-left" />
                </PageButton>
                <PageButton onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1 || !productsData || productsData.pages <= 1} title="Previous page">
                  <i className="bi bi-chevron-left" />
                </PageButton>
                <PageButton onClick={() => setCurrentPage(Math.min(productsData?.pages ?? 1, currentPage + 1))} disabled={!productsData || currentPage === productsData.pages || productsData.pages <= 1} title="Next page">
                  <i className="bi bi-chevron-right" />
                </PageButton>
                <PageButton onClick={() => setCurrentPage(productsData?.pages ?? 1)} disabled={!productsData || currentPage === productsData.pages || productsData.pages <= 1} title="Last page">
                  <i className="bi bi-chevron-double-right" />
                </PageButton>
              </PaginationControls>
            </PaginationFooter>
          )}
        </ProductsSection>

        <ProductFlyerModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          isAuthenticated={isAuthenticated}
          isAdmin={user?.is_admin || false}
          onLoginRequired={openLoginModal}
          categoryImageMap={categoryImageMap}
        />

        <LoginModal />
        <WshHelpTrigger />
      </PageWrapper>
    </>
  )
}

export default ProductsPage
